import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT, regions } from './layout';
import { makeTwoPassText } from './twoPassText';
import { makePawButton, FILL_OFF } from './standardButton';

export interface SettingsState {
  music: boolean;
  sfx: boolean;
  hints: boolean;
  // Mirrors iOS prefSkillLevel == 1 — when on, the best-square paw hint
  // pulses on every turn (otherwise it stays hidden).
  kidMode: boolean;
}

// Hex constants used by the confirmation dialog (still uses the small card).
const CARD_BG = 0xfff4cf;
const CARD_BORDER = 0xcc0000;

export class SettingsOverlay {
  readonly container: Phaser.GameObjects.Container;

  constructor(
    scene: Phaser.Scene,
    private state: SettingsState,
    private onChange: (next: SettingsState) => void,
    private onPlay: () => void,
    private onNewGame: () => void,
    private onReplayLevel: () => void,
  ) {
    this.container = scene.add.container(0, 0).setDepth(2000);
    const cx = LOGICAL_WIDTH / 2;
    const cy = LOGICAL_HEIGHT / 2;
    const r = regions();
    const cardX = r.board.x;
    const cardY = r.board.y;
    const cardW = r.board.width;
    const cardH = r.board.height + r.footer.height;

    // Header strip — small dim layer above the cover that dismisses on tap.
    const dim = scene.add
      .rectangle(0, 0, LOGICAL_WIDTH, r.header.height, 0x000000, 0.4)
      .setOrigin(0)
      .setInteractive();
    dim.on('pointerdown', () => this.onPlay());
    this.container.add(dim);

    // Full-cover settings panel — spans board + footer, partially transparent
    // so the underlying board reads through. Intercepts clicks so taps don't
    // bleed to the board's scene-level pointerdown listener.
    const card = scene.add
      .rectangle(cardX, cardY, cardW, cardH, 0x1f4711, 0.85)
      .setOrigin(0)
      .setInteractive();
    this.container.add(card);

    const title = makeTwoPassText(scene, cx, cardY + 80, 'Settings', {
      fontSize: '64px',
    });
    this.container.add([title.outline, title.fill]);

    // Toggle row helper — label + frame6 (ON) / frame5 (OFF) icon that flips
    // on tap. Both icons live at the same anchor; we toggle visibility.
    const makeRow = (label: string, y: number, key: keyof SettingsState): void => {
      const labelText = makeTwoPassText(scene, cx - 200, y, label, {
        fontSize: '56px',
        originX: 0,
        originY: 0.5,
      });

      const iconSize = 64;
      const iconX = cx + 140;
      const onIcon = scene.add
        .image(iconX, y, 'frame6')
        .setDisplaySize(iconSize, iconSize)
        .setOrigin(0.5)
        .setVisible(this.state[key]);
      const offIcon = scene.add
        .image(iconX, y, 'frame5')
        .setDisplaySize(iconSize, iconSize)
        .setOrigin(0.5)
        .setVisible(!this.state[key]);
      // One invisible hit-rect sits on top of both icons; clicking it flips
      // visibility. (Using the icons themselves as interactive would mean
      // tapping the hidden one wouldn't register.)
      const hit = scene.add
        .rectangle(iconX, y, iconSize, iconSize, 0x000000, 0)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        this.state[key] = !this.state[key];
        onIcon.setVisible(this.state[key]);
        offIcon.setVisible(!this.state[key]);
        this.onChange({ ...this.state });
      });

      this.container.add([labelText.outline, labelText.fill, onIcon, offIcon, hit]);
    };

    makeRow('Music', cy - 200, 'music');
    makeRow('SFX', cy - 120, 'sfx');
    makeRow('Hints', cy - 40, 'hints');
    makeRow('Kid Mode', cy + 40, 'kidMode');

    // Play! → Replay Level → New Game, top-to-bottom. Tightened spacing
    // (100px between centers) to leave clearance for the credits block.
    const play = makePawButton({
      scene,
      x: cx,
      y: cy + 140,
      label: 'Play!',
      fontSize: '40px',
      baseDepth: 2010,
      onClick: () => this.onPlay(),
    });
    this.container.add(play.objects);

    const replay = makePawButton({
      scene,
      x: cx,
      y: cy + 240,
      label: 'Replay Park',
      fontSize: '40px',
      baseDepth: 2010,
      onClick: () => this.onReplayLevel(),
    });
    this.container.add(replay.objects);

    const newGame = makePawButton({
      scene,
      x: cx,
      y: cy + 340,
      label: 'New Game',
      fontSize: '40px',
      baseDepth: 2010,
      onClick: () => this.showConfirmReset(scene),
    });
    this.container.add(newGame.objects);

    // Credits — Fredoka variable in yellow at a lighter weight (300) so it
    // reads thinner than the heavy display weight.
    const creditsTopY = cardY + cardH - 220;
    const credits: { text: string; size: number; weight: number; dy: number }[] = [
      { text: 'GrumpyOldMan Studios',         size: 34, weight: 500, dy: 0 },
      { text: 'grumpyoldman.co',              size: 24, weight: 300, dy: 42 },
      { text: 'Doug Grounds — Design & Tech', size: 24, weight: 300, dy: 86 },
      { text: 'Melissa Dodd — Art & Anims',   size: 24, weight: 300, dy: 122 },
      { text: 'Chance Dodd — Anim & Advice',  size: 24, weight: 300, dy: 158 },
    ];
    for (const c of credits) {
      const t = scene.add
        .text(cx, creditsTopY + c.dy, c.text, {
          font: `${c.weight} ${c.size}px Fredoka`,
          color: '#ffeb00',
        })
        .setOrigin(0.5, 0);
      this.container.add(t);
    }
  }

  private showConfirmReset(scene: Phaser.Scene): void {
    const cx = LOGICAL_WIDTH / 2;
    const cy = LOGICAL_HEIGHT / 2;
    const dlgW = 560;
    const dlgH = 320;

    const confirm = scene.add.container(0, 0).setDepth(2100);

    const dim = scene.add
      .rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x000000, 0.5)
      .setOrigin(0)
      .setInteractive();
    dim.on('pointerdown', () => confirm.destroy(true));
    confirm.add(dim);

    const card = scene.add
      .rectangle(cx, cy, dlgW, dlgH, CARD_BG, 1)
      .setStrokeStyle(8, CARD_BORDER)
      .setOrigin(0.5)
      .setInteractive();
    confirm.add(card);

    const prompt = makeTwoPassText(scene, cx, cy - 50, 'Reset the game and\nstart from the beginning?', {
      fontSize: '48px',
      align: 'center',
      lineSpacing: 8,
    });
    confirm.add([prompt.outline, prompt.fill]);

    const btnY = cy + 100;
    const btnDX = 140;

    const ok = makePawButton({
      scene,
      x: cx - btnDX,
      y: btnY,
      label: 'OK',
      fontSize: '36px',
      height: 64,
      baseDepth: 2110,
      onClick: () => {
        confirm.destroy(true);
        this.onNewGame();
      },
    });
    confirm.add(ok.objects);

    const no = makePawButton({
      scene,
      x: cx + btnDX,
      y: btnY,
      label: 'No',
      fontSize: '36px',
      height: 64,
      fillColor: FILL_OFF, // gray variant
      baseDepth: 2110,
      onClick: () => confirm.destroy(true),
    });
    confirm.add(no.objects);
  }

  destroy(): void {
    this.container.destroy(true);
  }
}
